import { Computed, SourceLine } from '@/features/report/Computed'
import { ConfidenceTag } from '@/features/report/ConfidenceTag'
import { actionForFinding, actionForIndicative } from '@/features/report/actions'
import { SignalBadge } from '@/components/SignalBadge'
import { Badge } from '@/components/ui/badge'
import { useI18n, type TranslationKey } from '@/i18n'
import type { Finding, IndicativeFinding } from '@/api/types'

/**
 * The anatomy of a finding card (BRIEF, surface 3). Every hard card carries,
 * top to bottom:
 *
 *   1. the traffic light — icon AND word, never colour alone
 *   2. the confidence tag — deterministisch/hoch, derived from the API lane
 *   3. the engine's own message
 *   4. the computed values, in IBM Plex Mono: `parameter = value ≤ limit`
 *   5. what to do about it
 *   6. rule ID · § source + document version · pack
 *
 * `break-inside: avoid` is on the article: on paper a finding never splits
 * across two pages.
 */
export function FindingCard({ finding }: { finding: Finding }) {
  const action = actionForFinding(finding.severity, finding.computed)

  return (
    <article
      data-testid="finding-card"
      data-kind="hard"
      data-severity={finding.severity}
      data-rule-id={finding.rule_id}
      className="flex h-full flex-col gap-3 rounded-md border border-border bg-white p-4 break-inside-avoid motion-safe:animate-fade"
    >
      <div className="flex flex-wrap items-start gap-2">
        <SignalBadge severity={finding.severity} withMeaning />
        <ConfidenceTag lane="finding" />
      </div>
      <p className="text-[14px] leading-snug text-ink">{finding.message}</p>
      <Computed computed={finding.computed} />
      <ActionNote actionKey={action.key} items={action.items} />
      <SourceLine ruleId={finding.rule_id} source={finding.source} pack={finding.pack} />
    </article>
  )
}

/**
 * An INDICATIVE output — dashed border, grey badge, no signal colour anywhere.
 *
 * This is the visual firebreak the brief demands: an unverified rule must never
 * be mistakable for a traffic light. There is no code path from here to
 * `SignalBadge`, because `IndicativeFinding` has no `severity` to give it, and
 * its confidence tag is `retrieval/mittel` by lane — not by inspection.
 */
export function IndicativeCard({ finding }: { finding: IndicativeFinding }) {
  const { t } = useI18n()
  const action = actionForIndicative()

  return (
    <article
      data-testid="indicative-card"
      data-kind="indicative"
      data-rule-id={finding.rule_id}
      className="flex h-full flex-col gap-3 rounded-md border border-dashed border-border-strong bg-surface p-4 break-inside-avoid"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="indicative">{t('report.indicativeBadge')}</Badge>
        <ConfidenceTag lane="indicative" />
        <code className="num text-[12px] text-muted-2">{finding.rule_status}</code>
      </div>
      <p className="text-[14px] leading-snug text-ink-2">{finding.message}</p>
      <p className="text-[12px] text-muted">
        <span className="text-muted-2">{t('report.indicativeReason')}:</span> {finding.reason}
      </p>
      <Computed computed={finding.computed} />
      <ActionNote actionKey={action.key} items={action.items} />
      <SourceLine ruleId={finding.rule_id} source={finding.source} pack={finding.pack} />
    </article>
  )
}

/**
 * "Was zu tun ist". Neutral by design — it never borrows a signal colour, since
 * the severity is already stated once, in the traffic light above it.
 */
function ActionNote({ actionKey, items }: { actionKey: TranslationKey; items?: string }) {
  const { t } = useI18n()

  return (
    <div data-testid="finding-action" className="mt-auto flex flex-col gap-0.5 text-[13px]">
      <span className="text-[12px] text-muted-2">{t('report.actionTitle')}</span>
      <p className="leading-snug text-ink-2">{t(actionKey, items ? { items } : undefined)}</p>
    </div>
  )
}
